import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { MATERIAL_BY_NAME } from '@/lib/planMaterials';
import { getTexture, hasTexture, STRETCH } from '@/lib/planTextures';

const shade = (hex, f) => {
  const n = parseInt((hex || '#888888').slice(1), 16);
  const r = Math.min(255, Math.round(((n >> 16) & 255) * f));
  const g = Math.min(255, Math.round(((n >> 8) & 255) * f));
  const b = Math.min(255, Math.round((n & 255) * f));
  return (r << 16) | (g << 8) | b;
};

// ---- procedural textures (256px canvases) ----
const _cache = {};
function noise(ctx, w, h, amt, alpha) {
  for (let i = 0; i < amt; i++) {
    const x = Math.random() * w, y = Math.random() * h, r = Math.random() * 2 + 0.5;
    ctx.fillStyle = `rgba(${Math.random() > 0.5 ? 255 : 0},${Math.random() > 0.5 ? 255 : 0},${Math.random() > 0.5 ? 255 : 0},${Math.random() * alpha})`;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  }
}
function makeCanvas(kind) {
  if (_cache[kind]) return _cache[kind];
  const c = document.createElement('canvas'); c.width = c.height = 256;
  const x = c.getContext('2d');
  const fill = (col) => { x.fillStyle = col; x.fillRect(0, 0, 256, 256); };
  const grid = (n, line, lw = 4) => { x.strokeStyle = line; x.lineWidth = lw; for (let i = 0; i <= n; i++) { const p = (i / n) * 256; x.beginPath(); x.moveTo(p, 0); x.lineTo(p, 256); x.moveTo(0, p); x.lineTo(256, p); x.stroke(); } };
  const bond = (rows, cols, base, mortar, jitter) => {
    fill(mortar); const bh = 256 / rows, bw = 256 / cols;
    for (let r = 0; r < rows; r++) for (let col = -1; col <= cols; col++) {
      const off = (r % 2) * (bw / 2);
      const jr = jitter ? (Math.random() * 20 - 10) : 0, jg = jitter ? (Math.random() * 16 - 8) : 0;
      const n = parseInt(base.slice(1), 16);
      const cc = `rgb(${Math.max(0, Math.min(255, ((n >> 16) & 255) + jr))},${Math.max(0, Math.min(255, ((n >> 8) & 255) + jg))},${Math.max(0, Math.min(255, (n & 255) + jr))})`;
      x.fillStyle = cc; x.fillRect(col * bw + off + 2, r * bh + 2, bw - 4, bh - 4);
    }
  };
  switch (kind) {
    case 'sandstone': fill('#c9b48f'); noise(x, 256, 256, 1400, 0.12); grid(3, 'rgba(90,70,40,0.5)', 5); break;
    case 'porcelain': fill('#d9d4cc'); noise(x, 256, 256, 500, 0.05); grid(3, 'rgba(120,115,105,0.5)', 3); break;
    case 'concslab': fill('#c8c7c0'); noise(x, 256, 256, 900, 0.08); grid(4, 'rgba(120,120,115,0.45)', 3); break;
    case 'concrete': fill('#c4c4be'); noise(x, 256, 256, 900, 0.08); grid(2, 'rgba(120,120,115,0.35)', 3); break;
    case 'block': bond(6, 5, '#9aa4b0', '#6b7280', true); break;
    case 'brick': bond(8, 4, '#9c4a3a', '#c9c3b8', true); break;
    case 'concblock': bond(4, 2, '#b8bcc0', '#8f9498', false); break;
    case 'render': fill('#e7e2d8'); noise(x, 256, 256, 2600, 0.06); break;
    case 'resin': fill('#6b7280'); for (let i = 0; i < 3000; i++) { const g = 90 + Math.random() * 90; x.fillStyle = `rgb(${g},${g - 6},${g - 12})`; x.beginPath(); x.arc(Math.random() * 256, Math.random() * 256, Math.random() * 3 + 1, 0, 6.3); x.fill(); } break;
    case 'gravel': fill('#d6cdbf'); for (let i = 0; i < 2400; i++) { const g = 150 + Math.random() * 90; x.fillStyle = `rgb(${g},${g - 10},${g - 24})`; x.beginPath(); x.arc(Math.random() * 256, Math.random() * 256, Math.random() * 4 + 1.5, 0, 6.3); x.fill(); } break;
    case 'tarmac': fill('#2f3336'); for (let i = 0; i < 3000; i++) { const g = 40 + Math.random() * 50; x.fillStyle = `rgb(${g},${g},${g})`; x.fillRect(Math.random() * 256, Math.random() * 256, 2, 2); } break;
    case 'turf': fill('#5aa469'); for (let i = 0; i < 2600; i++) { const g = 120 + Math.random() * 90; x.strokeStyle = `rgba(${g - 70},${g},${g - 80},0.5)`; x.lineWidth = 1; const px = Math.random() * 256, py = Math.random() * 256; x.beginPath(); x.moveTo(px, py); x.lineTo(px + (Math.random() * 4 - 2), py - (Math.random() * 6 + 2)); x.stroke(); } break;
    case 'artificial': fill('#3f8f57'); x.strokeStyle = 'rgba(60,130,80,0.5)'; x.lineWidth = 1; for (let px = 0; px < 256; px += 3) { x.beginPath(); x.moveTo(px, 0); x.lineTo(px, 256); x.stroke(); } break;
    case 'planting': fill('#5b3f2a'); noise(x, 256, 256, 1200, 0.12); for (let i = 0; i < 500; i++) { x.fillStyle = `rgba(60,${120 + Math.random() * 60},50,0.6)`; x.beginPath(); x.arc(Math.random() * 256, Math.random() * 256, Math.random() * 5 + 2, 0, 6.3); x.fill(); } break;
    case 'hedge': fill('#3f7a44'); for (let i = 0; i < 2200; i++) { const g = 90 + Math.random() * 90; x.fillStyle = `rgba(${g - 50},${g},${g - 55},0.7)`; x.beginPath(); x.arc(Math.random() * 256, Math.random() * 256, Math.random() * 6 + 2, 0, 6.3); x.fill(); } break;
    case 'decking': fill('#8a5a2b'); { const planks = 6, ph = 256 / planks; for (let i = 0; i < planks; i++) { const g = 150 + Math.random() * 40; x.fillStyle = `rgb(${g},${g - 40},${g - 80})`; x.fillRect(0, i * ph + 2, 256, ph - 4); x.strokeStyle = 'rgba(80,50,20,0.4)'; for (let s2 = 0; s2 < 6; s2++) { x.beginPath(); x.moveTo(0, i * ph + Math.random() * ph); x.bezierCurveTo(85, i * ph + Math.random() * ph, 170, i * ph + Math.random() * ph, 256, i * ph + Math.random() * ph); x.stroke(); } } } break;
    case 'sleeper': fill('#7a5a37'); { const planks = 4, ph = 256 / planks; for (let i = 0; i < planks; i++) { const g = 110 + Math.random() * 30; x.fillStyle = `rgb(${g},${g - 25},${g - 55})`; x.fillRect(0, i * ph + 3, 256, ph - 6); } x.strokeStyle = 'rgba(50,35,18,0.4)'; x.lineWidth = 1; for (let s2 = 0; s2 < 40; s2++) { const yy = Math.random() * 256; x.beginPath(); x.moveTo(0, yy); x.lineTo(256, yy + (Math.random() * 6 - 3)); x.stroke(); } } break;
    case 'timber': fill('#a9743b'); x.strokeStyle = 'rgba(80,50,20,0.35)'; x.lineWidth = 1; for (let py = 0; py < 256; py += 6) { x.beginPath(); x.moveTo(0, py + Math.random() * 3); x.lineTo(256, py + Math.random() * 3); x.stroke(); } break;
    case 'slate': fill('#55585c'); bond(8, 5, '#55585c', '#3c3f42', true); break;
    case 'glass': fill('#cfe6ea'); break;
    case 'water': fill('#3b82c4'); for (let i = 0; i < 40; i++) { x.strokeStyle = 'rgba(255,255,255,0.15)'; x.lineWidth = 2; x.beginPath(); x.moveTo(0, Math.random() * 256); x.bezierCurveTo(85, Math.random() * 256, 170, Math.random() * 256, 256, Math.random() * 256); x.stroke(); } break;
    default: fill('#cccccc');
  }
  _cache[kind] = c; return c;
}
// rx/ry are metres for box faces, or 1 for extrudes (whose UVs are already in metres)
function tex(kind, rx = 1, ry = 1, color) {
  const hq = hasTexture(kind) && !STRETCH.has(kind);
  const t = new THREE.CanvasTexture(hq ? getTexture(kind, color).canvas : makeCanvas(kind));
  const tm = hq ? getTexture(kind, color).tileM : 1;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(rx / tm, ry / tm);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}
const stdTop = (kind, rx, ry, color) => new THREE.MeshStandardMaterial({ map: tex(kind, rx, ry, color), roughness: 0.95, metalness: 0 });
const stdCol = (hex, f = 1) => new THREE.MeshStandardMaterial({ color: shade(hex, f), roughness: 0.9 });

function roundedRectShape(w, h, r) {
  const s = new THREE.Shape();
  const x = -w / 2, y = -h / 2;
  r = Math.max(0, Math.min(r, Math.min(w, h) / 2 - 0.001));
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r); s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h); s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

// meshes are built CENTRED at local origin; caller positions + rotates the group
function buildShapeMesh(s) {
  const mat = MATERIAL_BY_NAME[s.name] || {};
  const kind = mat.tex || 'concrete';
  const zH = Math.max(0.03, s.z || 0.1);
  const group = new THREE.Group();

  if (mat.tree) {
    const r = Math.min(s.w, s.h) / 2, hgt = Math.max(0.8, s.z || 3);
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.07, r * 0.1, hgt * 0.45, 8), stdCol('#6b4a2f'));
    trunk.position.y = hgt * 0.22; trunk.castShadow = true; group.add(trunk);
    const canopyMat = new THREE.MeshStandardMaterial({ color: shade(s.color, 1), roughness: 0.9, flatShading: true });
    [[0, 0.62, 0, 1], [r * 0.35, 0.55, r * 0.2, 0.7], [-r * 0.3, 0.58, -r * 0.25, 0.72]].forEach(([dx, fy, dz, f]) => {
      const c = new THREE.Mesh(new THREE.IcosahedronGeometry(r * f * 0.85, 1), canopyMat);
      c.position.set(dx, hgt * fy + r * 0.25, dz); c.castShadow = true; c.receiveShadow = true; group.add(c);
    });
    return group;
  }
  if (mat.roof) {
    const wallH = zH * 0.62, roofH = zH - wallH;
    const body = new THREE.Mesh(new THREE.BoxGeometry(s.w, wallH, s.h), stdCol('#e7e2d8', 0.95));
    body.position.y = wallH / 2; body.castShadow = true; body.receiveShadow = true; group.add(body);
    const roof = new THREE.Mesh(new THREE.ConeGeometry(Math.SQRT1_2, 1, 4, 1), new THREE.MeshStandardMaterial({ map: tex(kind, 3, 3, s.color), roughness: 0.9, flatShading: true }));
    roof.rotation.y = Math.PI / 4; roof.scale.set(s.w * 1.04, roofH, s.h * 1.04); roof.position.y = wallH + roofH / 2;
    roof.castShadow = true; group.add(roof);
    return group;
  }
  if (kind === 'table') {
    const top = new THREE.Mesh(new THREE.CylinderGeometry(s.w * 0.25, s.w * 0.25, 0.04, 32), stdCol('#e8e2d6'));
    top.position.y = 0.74; top.castShadow = true; group.add(top);
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.06, 0.72, 12), stdCol('#5b6168'));
    leg.position.y = 0.36; group.add(leg);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2, r = s.w * 0.37;
      const seat = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.45, 0.42), stdCol('#5b6168'));
      seat.position.set(Math.cos(a) * r, 0.225, Math.sin(a) * r); seat.rotation.y = -a; seat.castShadow = true; group.add(seat);
    }
    return group;
  }
  if (STRETCH.has(kind)) {
    const t = new THREE.CanvasTexture(getTexture(kind, s.color).canvas); t.colorSpace = THREE.SRGBColorSpace;
    const top = new THREE.MeshStandardMaterial({ map: t, transparent: true, roughness: 0.8 });
    const geo = s.type === 'circle' ? new THREE.CylinderGeometry(s.w / 2, s.w / 2, zH, 32) : new THREE.BoxGeometry(s.w, zH, s.h);
    const side = stdCol(s.color, 0.8);
    const m = new THREE.Mesh(geo, s.type === 'circle' ? [side, top, side] : [side, side, top, side, side, side]);
    if (s.type === 'circle') m.scale.z = s.h / s.w;
    m.position.y = zH / 2; m.castShadow = true; m.receiveShadow = true; group.add(m);
    return group;
  }

  // freehand polygon area (points relative to bbox top-left)
  if (s.type === 'poly' && s.points?.length >= 3) {
    const shp = new THREE.Shape();
    s.points.forEach((p, i) => { const x = p.x - s.w / 2, y = p.y - s.h / 2; if (i === 0) shp.moveTo(x, y); else shp.lineTo(x, y); });
    shp.closePath();
    const geo = new THREE.ExtrudeGeometry(shp, { depth: zH, bevelEnabled: false });
    geo.rotateX(-Math.PI / 2);
    const top = new THREE.MeshStandardMaterial({ map: tex(kind, 1, 1, s.color), roughness: 0.95 });
    const m = new THREE.Mesh(geo, [top, stdCol(s.color, 0.8)]);
    m.castShadow = true; m.receiveShadow = true; group.add(m);
    return group;
  }

  if (s.type === 'circle') {
    const rx = s.w / 2;
    const geo = new THREE.CylinderGeometry(rx, rx, zH, 40);
    const m = new THREE.Mesh(geo, [stdCol(s.color, 0.8), stdTop(kind, s.w, s.h), stdCol(s.color, 0.6)]);
    m.scale.set(1, 1, (s.h / s.w) || 1);
    m.position.set(0, zH / 2, 0);
    m.castShadow = true; m.receiveShadow = true; group.add(m);
    return group;
  }

  if (mat.structure === 'shed' || mat.structure === 'summerhouse') {
    const bodyH = zH * 0.62, roofH = zH - bodyH;
    const body = new THREE.Mesh(new THREE.BoxGeometry(s.w, bodyH, s.h),
      [stdTop('timber', s.h, bodyH), stdTop('timber', s.h, bodyH), stdCol(s.color, 1.05), stdCol(s.color, 0.5), stdTop('timber', s.w, bodyH), stdTop('timber', s.w, bodyH)]);
    body.position.set(0, bodyH / 2, 0); body.castShadow = true; body.receiveShadow = true; group.add(body);
    const shp = new THREE.Shape(); shp.moveTo(-s.w / 2, 0); shp.lineTo(s.w / 2, 0); shp.lineTo(0, roofH); shp.closePath();
    const rgeo = new THREE.ExtrudeGeometry(shp, { depth: s.h, bevelEnabled: false }); rgeo.translate(0, 0, -s.h / 2);
    const roof = new THREE.Mesh(rgeo, stdTop('slate', s.w, s.h)); roof.position.set(0, bodyH, 0);
    roof.castShadow = true; roof.receiveShadow = true; group.add(roof);
    return group;
  }

  if (mat.structure === 'greenhouse') {
    const box = new THREE.Mesh(new THREE.BoxGeometry(s.w, zH, s.h), new THREE.MeshStandardMaterial({ color: 0xcfe6ea, transparent: true, opacity: 0.35, roughness: 0.1, metalness: 0.1 }));
    box.position.set(0, zH / 2, 0); group.add(box);
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(box.geometry), new THREE.LineBasicMaterial({ color: 0x4b5563 }));
    edges.position.copy(box.position); group.add(edges);
    return group;
  }

  if (mat.structure === 'pergola') {
    const post = 0.1, mcol = stdTop('timber', 1, 1);
    [[-s.w / 2 + post, -s.h / 2 + post], [s.w / 2 - post, -s.h / 2 + post], [-s.w / 2 + post, s.h / 2 - post], [s.w / 2 - post, s.h / 2 - post]]
      .forEach(([cx, cy]) => { const p = new THREE.Mesh(new THREE.BoxGeometry(post, zH, post), mcol); p.position.set(cx, zH / 2, cy); p.castShadow = true; group.add(p); });
    for (let i = 0; i <= 4; i++) { const beam = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.12, s.h), mcol); beam.position.set(-s.w / 2 + (i / 4) * s.w, zH, 0); beam.castShadow = true; group.add(beam); }
    return group;
  }

  if (mat.tex === 'water') {
    const base = new THREE.Mesh(new THREE.BoxGeometry(s.w, 0.15, s.h), stdCol('#334155', 0.9));
    base.position.set(0, 0.075, 0); base.receiveShadow = true; group.add(base);
    const water = new THREE.Mesh(new THREE.BoxGeometry(s.w * 0.9, 0.16, s.h * 0.9), new THREE.MeshStandardMaterial({ map: tex('water', 1, 1), transparent: true, opacity: 0.85, roughness: 0.15, metalness: 0.3 }));
    water.position.set(0, 0.11, 0); group.add(water);
    return group;
  }

  // curved / bowed wall — segmented boxes following a quadratic arc
  if ((mat.wall || mat.canBow) && s.bow) {
    const span = s.w, th = s.h, bow = s.bow, N = 18;
    const P = (t) => [span * (t - 0.5), 2 * (1 - t) * t * (-bow * span / 2)];
    for (let i = 0; i < N; i++) {
      const a = P(i / N), b = P((i + 1) / N);
      const dx = b[0] - a[0], dz = b[1] - a[1], segLen = Math.hypot(dx, dz) + 0.02;
      const seg = new THREE.Mesh(new THREE.BoxGeometry(segLen, zH, th), stdTop(kind, Math.max(0.4, segLen), zH));
      seg.position.set((a[0] + b[0]) / 2, zH / 2, (a[1] + b[1]) / 2);
      seg.rotation.y = Math.atan2(-dz, dx);
      seg.castShadow = true; seg.receiveShadow = true; group.add(seg);
    }
    return group;
  }

  // straight wall
  if (mat.wall) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(s.w, zH, s.h), [
      stdTop(kind, s.h, zH), stdTop(kind, s.h, zH), stdCol(s.color, 1.08), stdCol(s.color, 0.5), stdTop(kind, s.w, zH), stdTop(kind, s.w, zH),
    ]);
    m.position.set(0, zH / 2, 0); m.castShadow = true; m.receiveShadow = true; group.add(m);
    return group;
  }

  // rounded-corner flat area (curve>0) via extruded rounded rect
  if (s.curve > 0) {
    const r = (s.curve || 0) * Math.min(s.w, s.h) / 2;
    const geo = new THREE.ExtrudeGeometry(roundedRectShape(s.w, s.h, r), { depth: zH, bevelEnabled: false });
    geo.rotateX(-Math.PI / 2);
    const top = new THREE.MeshStandardMaterial({ map: tex(kind, 1, 1, s.color), roughness: 0.95 });
    const m = new THREE.Mesh(geo, [top, stdCol(s.color, 0.8)]);
    m.castShadow = true; m.receiveShadow = true; group.add(m);
    return group;
  }

  // flat ground area (box)
  const m = new THREE.Mesh(new THREE.BoxGeometry(s.w, zH, s.h), [
    stdCol(s.color, 0.8), stdCol(s.color, 0.8), stdTop(kind, s.w, s.h, s.color), stdCol(s.color, 0.5), stdCol(s.color, 0.8), stdCol(s.color, 0.8),
  ]);
  m.position.set(0, zH / 2, 0); m.castShadow = true; m.receiveShadow = true; group.add(m);
  return group;
}

const Plan3D = forwardRef(function Plan3D({ shapes, plotW, plotL, ground = 'plain' }, ref) {
  const mount = useRef(null);
  const state = useRef({});

  useImperativeHandle(ref, () => ({
    getSnapshot: () => {
      const s = state.current;
      if (!s.renderer) return null;
      s.renderer.render(s.scene, s.camera);
      try { return s.renderer.domElement.toDataURL('image/png'); } catch { return null; }
    },
  }));

  // init once
  useEffect(() => {
    const el = mount.current; if (!el) return;
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    } catch {
      el.innerHTML = '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#64748b;font-size:13px;text-align:center;padding:16px">3D preview needs a browser with WebGL enabled.</div>';
      return;
    }
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xdfeaf2);
    const w = el.clientWidth || 640, h = el.clientHeight || 460;
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 1000);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFShadowMap;
    el.appendChild(renderer.domElement);

    const hemi = new THREE.HemisphereLight(0xffffff, 0x8899aa, 0.9); scene.add(hemi);
    const sun = new THREE.DirectionalLight(0xfff5e6, 1.15);
    sun.castShadow = true; sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 0.5; sun.shadow.camera.far = 200;
    Object.assign(sun.shadow.camera, { left: -40, right: 40, top: 40, bottom: -40 });
    scene.add(sun);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true; controls.dampingFactor = 0.08;
    controls.maxPolarAngle = Math.PI / 2.05;

    const worldGroup = new THREE.Group(); scene.add(worldGroup);

    let raf;
    const animate = () => { raf = requestAnimationFrame(animate); controls.update(); renderer.render(scene, camera); };
    animate();

    const ro = new ResizeObserver(() => {
      const nw = el.clientWidth, nh = el.clientHeight; if (!nw || !nh) return;
      camera.aspect = nw / nh; camera.updateProjectionMatrix(); renderer.setSize(nw, nh);
    });
    ro.observe(el);

    state.current = { scene, camera, renderer, controls, worldGroup, sun, raf, ro, el };
    return () => {
      cancelAnimationFrame(state.current.raf); ro.disconnect(); controls.dispose(); renderer.dispose();
      if (renderer.domElement.parentNode === el) el.removeChild(renderer.domElement);
    };
  }, []);

  // rebuild scene contents when the plan changes
  useEffect(() => {
    const s = state.current; if (!s.scene) return;
    const { worldGroup, controls, camera, sun } = s;
    while (worldGroup.children.length) { const c = worldGroup.children.pop(); c.traverse?.((o) => { o.geometry?.dispose?.(); if (Array.isArray(o.material)) o.material.forEach((m) => m.map?.dispose?.() || m.dispose?.()); else { o.material?.map?.dispose?.(); o.material?.dispose?.(); } }); worldGroup.remove(c); }

    // ground plot
    const textured = ground !== 'plain';
    const groundMat = textured ? new THREE.MeshStandardMaterial({ map: tex(ground, plotW, plotL), roughness: 1 }) : new THREE.MeshStandardMaterial({ color: 0xeef2f6, roughness: 1 });
    const plot = new THREE.Mesh(new THREE.BoxGeometry(plotW, 0.06, plotL), [stdCol('#8a7a66'), stdCol('#8a7a66'), groundMat, stdCol('#8a7a66'), stdCol('#8a7a66'), stdCol('#8a7a66')]);
    plot.position.set(plotW / 2, -0.03, plotL / 2); plot.receiveShadow = true; worldGroup.add(plot);
    if (!textured) {
      const grid = new THREE.GridHelper(Math.max(plotW, plotL), Math.max(plotW, plotL), 0xb8c2cc, 0xd4dce3);
      grid.position.set(plotW / 2, 0.005, plotL / 2); grid.scale.set(plotW / Math.max(plotW, plotL), 1, plotL / Math.max(plotW, plotL)); worldGroup.add(grid);
    }
    const border = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(plotW, 0.07, plotL)), new THREE.LineBasicMaterial({ color: 0x0f172a })); border.position.set(plotW / 2, 0, plotL / 2); worldGroup.add(border);

    (shapes || []).forEach((sh) => {
      const g = buildShapeMesh(sh);
      g.position.set(sh.x + sh.w / 2, 0, sh.y + sh.h / 2);
      g.rotation.y = -((sh.rot || 0) * Math.PI / 180);
      worldGroup.add(g);
    });

    // frame camera + sun to the plot
    const span = Math.max(plotW, plotL, 4);
    controls.target.set(plotW / 2, 0, plotL / 2);
    camera.position.set(plotW / 2 + span * 0.9, span * 0.9, plotL / 2 + span * 1.1);
    sun.position.set(plotW / 2 + span, span * 1.4, plotL / 2 - span * 0.4);
    sun.target.position.set(plotW / 2, 0, plotL / 2); s.scene.add(sun.target);
    controls.update();
  }, [shapes, plotW, plotL, ground]);

  return <div ref={mount} data-testid="plan-3d-canvas" className="w-full h-[460px] bg-[#dfeaf2]" style={{ touchAction: 'none' }} />;
});

export default Plan3D;
